import { useApiQuery } from './useApiQuery';

export interface ShiftEngagement {
  id: number;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  email: string;
  dob: string;
  gender: 'MALE' | 'FEMALE';
  reminder: boolean;
  partner: {
    id: number;
    name: string;
    orgType: string;
    partnerIdentifier: string;
    legalName: string;
    contact: string;
    website: string;
    createdAt: string;
    updatedAt: string;
    enabledModules: string[];
  };
  isRegistered: boolean;
  actor: {
    id: number;
    email: string;
    fullName: string;
    role: string;
    partner: any;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
    lastLoginIp: string;
    lastLoginLocation: string;
    lastLoginAt: string;
  };
  shift: {
    id: string;
    actor: any;
    partner: any;
    status: string;
    startedAt: string;
    endedAt: string | null;
    lastHeartbeatAt: string;
    startLatitude: number;
    startLongitude: number;
    endLatitude: number | null;
    endLongitude: number | null;
    endReason: string | null;
    lastPausedAt: string | null;
    numberOfPauses: number | null;
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ShiftEngagementsResponse {
  success: boolean;
  message: string;
  data: ShiftEngagement[];
}

export function useShiftEngagements(shiftId: string | undefined) {
  return useApiQuery<ShiftEngagementsResponse>({
    queryKey: ['shift-engagements', shiftId],
    request: {
      url: `/partner/shifts/${shiftId}/engagements`,
      method: 'GET',
    },
    enabled: !!shiftId,
  });
}

// Helper function to calculate statistics from engagements
export function calculateEngagementStats(engagements: ShiftEngagement[]) {
  const totalRegistrations = engagements.length;
  
  // Calculate today's registrations
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayRegistrations = engagements.filter(engagement => {
    const engagementDate = new Date(engagement.createdAt);
    engagementDate.setHours(0, 0, 0, 0); 
    return engagementDate.getTime() === today.getTime();
  }).length;

  // Calculate registered vs unregistered
  const registeredCount = engagements.filter(e => e.isRegistered).length;
  const unregisteredCount = totalRegistrations - registeredCount;

  // Calculate reminder opt-ins
  const reminderCount = engagements.filter(e => e.reminder).length;

  return {
    totalRegistrations,
    todayRegistrations,
    registeredCount,
    unregisteredCount,
    reminderCount,
    registrationRate: totalRegistrations > 0 ? (registeredCount / totalRegistrations) * 100 : 0,
  };
}
